import React, { useState, useEffect, useCallback } from "react";
import { Container, Col, Row, Spinner } from "react-bootstrap";
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import axios from "axios";

import PublicHeaderLoan from "../components/header/PublicHeaderLoan";
import DueDates from "../components/misc/DueDates";
import ToPeso from "../components/misc/ToPeso";
import GLOBALVAR from "../constants";
import NotFoundPage from "./NotFoundPage";

import '../css/loan-page.scss';
import '../css/public-header-loan.scss';

function PublicLoanPage() {
    const { timestamp, loanId } = useParams();
    const [loan, setLoan] = useState(null);
    const [paymentList, setPaymentList] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [penaltyData, setPenaltyData] = useState(null);

    const fetchLoan = useCallback(async (id) => {
        try {
            setIsLoading(true);
            setError(null);
            
            // Validate URL parameters
            if (!id || !timestamp) {
                setError('Invalid URL parameters');
                return;
            }

            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/${id}`);
            const loanData = response.data;
            
            if (!loanData) {
                setError('Loan not found');
                return;
            }

            // Verify if the timestamp matches the loan's dateGranted
            const loanTimestamp = new Date(loanData.dateGranted).getTime();
            const urlTimestamp = parseInt(timestamp);
            
            if (loanTimestamp !== urlTimestamp) {
                setError('Invalid loan link');
                return;
            }

            setPaymentList(loanData.payments);
            setLoan(loanData);
        } catch (error) {
            console.error('Error fetching loan:', error);
            if (error.response?.status === 404) {
                setError('Loan not found');
            } else {
                setError(error.response?.data?.message || 'Error fetching loan details');
            }
        } finally {
            setIsLoading(false);
        }
    }, [timestamp]);

    const fetchPenalties = async (id) => {
        try {
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/penalties/loan/${id}`);
            
            // Process the response to calculate consolidated values
            if (response.data && response.data.data) {
                const penaltyRecords = response.data.data;
                
                // Step 1: Filter unpaid penalties
                const unpaidPenalties = penaltyRecords.filter(penalty => !penalty.isPaid);
                
                // Step 2: Group unpaid penalties by lastPaymentDate
                const groupedByDate = {};
                unpaidPenalties.forEach(penalty => {
                    const paymentDate = penalty.lastPaymentDate || 'unknown';
                    if (!groupedByDate[paymentDate]) {
                        groupedByDate[paymentDate] = [];
                    }
                    groupedByDate[paymentDate].push(penalty);
                });
                
                // Step 3: Get only the latest record for each lastPaymentDate group
                const latestRecordsPerDate = Object.values(groupedByDate).map(group => {
                    // Sort by createdAt or calculationDate (newest first) and take the first item
                    return group.sort((a, b) => {
                        const dateA = new Date(a.createdAt || a.calculationDate);
                        const dateB = new Date(b.createdAt || b.calculationDate);
                        return dateB - dateA;
                    })[0];
                });
                
                // Step 4: Calculate total unpaid amount using the filtered records
                const totalUnpaidAmount = latestRecordsPerDate.reduce((sum, penalty) => {
                    const penaltyAmount = parseFloat(penalty.penaltyAmount || 0);
                    const paidAmount = parseFloat(penalty.paidAmount || 0);
                    return sum + (penaltyAmount - paidAmount);
                }, 0);
                
                // Calculate filtered total penalty amount (sum of all penalty amounts from filtered records)
                const filteredTotalPenaltyAmount = latestRecordsPerDate.reduce((sum, penalty) => {
                    return sum + parseFloat(penalty.penaltyAmount || 0);
                }, 0);
                
                // Calculate filtered total paid amount (sum of all paid amounts from filtered records)
                const filteredTotalPaidAmount = latestRecordsPerDate.reduce((sum, penalty) => {
                    return sum + parseFloat(penalty.paidAmount || 0);
                }, 0);
                
                // Calculate the outstanding balance using the filtered totals
                const outstandingBalance = filteredTotalPenaltyAmount - filteredTotalPaidAmount;
                
                // Enhance the response with additional calculated values
                const enhancedData = {
                    ...response.data,
                    consolidatedData: {
                        unpaidCount: latestRecordsPerDate.length, // Updated to use filtered records count
                        totalUnpaidAmount: totalUnpaidAmount.toFixed(2),
                        outstandingBalance: outstandingBalance.toFixed(2),
                        filteredTotalPenaltyAmount: filteredTotalPenaltyAmount.toFixed(2),
                        filteredTotalPaidAmount: filteredTotalPaidAmount.toFixed(2),
                        oldestPenaltyDate: latestRecordsPerDate.length > 0 ? 
                            Math.min(...latestRecordsPerDate.map(p => new Date(p.calculationDate).getTime())) : null,
                        filteredRecords: latestRecordsPerDate // Include filtered records for reference
                    }
                };
                
                setPenaltyData(enhancedData);
            } else {
                setPenaltyData(response.data);
            }
        } catch (error) {
            console.error('Error fetching penalties:', error);
        }
    };

    useEffect(() => {
        if (loanId) {
            fetchLoan(loanId);
            fetchPenalties(loanId);
        }
    }, [loanId, fetchLoan]);

    if (error) {
        return <NotFoundPage />;
    }

    return (
        <div className="main-content loan-page loan-page-single">
            <PublicHeaderLoan loan={loan} />
            
            {isLoading ? (
                <div className="loader-container d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                    <div className="text-center">
                        <Spinner animation="border" role="status" variant="primary" />
                        <p className="mt-2 text-muted">Loading loan details...</p>
                    </div>
                </div>
            ) : (
                <Container className="parent-container">
                    <Row>
                    <Col lg={7}>
                            <div className="payment-section payment-duedates">
                                {loan && (
                                    <>
                                        <h5 className="section-title">
                                            <i className="fa-solid fa-calendar-days"></i>
                                            Due Dates ({loan.loanInterval})
                                        </h5>
                                        <DueDates 
                                            date={loan.dateGranted} 
                                            interval={loan.loanInterval} 
                                            term={loan.loanTerm} 
                                            amount={loan.amountGranted} 
                                            interest={loan.interestRate} 
                                            paymentList={paymentList} 
                                            loanItem={loan} 
                                        />
                                    </>
                                )}
                            </div>

                            <div className="payment-section penalty-history">
                                <h5 className="section-title">
                                    <i className="fa-solid fa-triangle-exclamation"></i>
                                    Penalty Records
                                </h5>
                                {penaltyData && penaltyData.data?.length > 0 ? (
                                    <div className="penalty-consolidated-view">
                                        <div className="penalty-summary">
                                            <div className="summary-item">
                                                <span className="label">Total Penalties:</span>
                                                <span className="value">
                                                    {penaltyData.consolidatedData?.filteredTotalPenaltyAmount ? 
                                                        <ToPeso amount={penaltyData.consolidatedData.filteredTotalPenaltyAmount} /> : 
                                                        <ToPeso amount={penaltyData.summary.totalPenaltyAmount} />
                                                    }
                                                </span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="label">Total Penalties Paid:</span>
                                                <span className="value">
                                                    {penaltyData.consolidatedData?.filteredTotalPaidAmount ? 
                                                        <ToPeso amount={penaltyData.consolidatedData.filteredTotalPaidAmount} /> : 
                                                        <ToPeso amount={penaltyData.summary.totalPaidAmount} />
                                                    }
                                                </span>
                                            </div>
                                            <div className="summary-item">
                                                <span className="label">Outstanding Penalties:</span>
                                                <span className="value">
                                                    {penaltyData.consolidatedData?.outstandingBalance ? 
                                                        <ToPeso amount={penaltyData.consolidatedData.outstandingBalance} /> : 
                                                        <ToPeso amount={
                                                            parseFloat(penaltyData.summary.totalPenaltyAmount) - 
                                                            parseFloat(penaltyData.summary.totalPaidAmount)
                                                        } />
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="penalty-details">
                                            <div className="detail-grid">
                                                <div className="detail-item">
                                                    <span className="label">Total Records:</span>
                                                    <span className="value">{penaltyData.count}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="label">Paid Records:</span>
                                                    <span className="value">{penaltyData.summary.paidPenalties}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="label">Unpaid Records:</span>
                                                    <span className="value">{penaltyData.count - penaltyData.summary.paidPenalties}</span>
                                                </div>
                                                {penaltyData.consolidatedData?.unpaidCount > 0 && (
                                                    <div className="detail-item">
                                                        <span className="label">Oldest Unpaid Penalty:</span>
                                                        <span className="value">
                                                            {penaltyData.consolidatedData.oldestPenaltyDate ? 
                                                                format(new Date(penaltyData.consolidatedData.oldestPenaltyDate), 'MMM dd, yyyy') : 
                                                                'N/A'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="no-payments-message">
                                        <div className="message-content">
                                            <i className="fa-solid fa-check-circle"></i>
                                            <h6>No Penalties</h6>
                                            <p>This loan has no penalty records.</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="payment-section payment-history">
                                <h5 className="section-title">
                                    <i className="fa-solid fa-clock-rotate-left"></i>
                                    Payment Records
                                </h5>
                                {paymentList?.length > 0 ? (
                                    paymentList.map((item, index) => (
                                        <div className="payment-list__item" key={index}>
                                            <div className="payment-list__item-header">
                                                <div className="payment-list__item-main">
                                                    <div className="payment-date">
                                                        <i className="fa-solid fa-calendar"></i>
                                                        {item.paymentDate ? format(new Date(item.paymentDate), 'MMMM dd, yyyy') : 'N/A'}
                                                    </div>
                                                    <div className="payment-amount">
                                                        <i className="fa-solid fa-peso-sign"></i>
                                                        <ToPeso amount={item.paidTotal || 0} />
                                                    </div>
                                                </div>
                                                <div className="payment-list__item-actions">
                                                    <div className="payment-type">
                                                        <span className={`type-badge ${item.paymentType?.toLowerCase() || 'cash'}`}>
                                                            <i className={`fa-solid ${
                                                                item.paymentType?.toLowerCase() === 'cash' ? 'fa-money-bill' :
                                                                item.paymentType?.toLowerCase() === 'cheque' ? 'fa-receipt' :
                                                                'fa-credit-card'
                                                            }`}></i>
                                                            {item.paymentType || 'Cash'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="payment-list__item-details">
                                                <div className="payment-breakdown">
                                                    <h6>Payment Breakdown</h6>
                                                    <div className="breakdown-grid">
                                                        <div className="breakdown-item">
                                                            <span className="label">Principal</span>
                                                            <span className="amount">
                                                                <ToPeso amount={item.paidPrincipal || 0} />
                                                            </span>
                                                        </div>
                                                        <div className="breakdown-item">
                                                            <span className="label">Interest</span>
                                                            <span className="amount">
                                                                <ToPeso amount={item.paidInterest || 0} />
                                                            </span>
                                                        </div>
                                                        <div className="breakdown-item">
                                                            <span className="label">Penalty</span>
                                                            <span className="amount">
                                                                <ToPeso amount={item.paidPenalty || 0} />
                                                            </span>
                                                        </div>
                                                        <div className="breakdown-item">
                                                            <span className="label">Other</span>
                                                            <span className="amount">
                                                                <ToPeso amount={item.paidOther || 0} />
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="payment-references">
                                                    <h6>Payment References</h6>
                                                    <div className="reference-grid">
                                                        <div className="reference-item">
                                                            <span className="label">PR No.</span>
                                                            <span className="value">{item.prNumber || 'N/A'}</span>
                                                        </div>
                                                        <div className="reference-item">
                                                            <span className="label">OR No.</span>
                                                            <span className="value">{item.orNumber || 'N/A'}</span>
                                                        </div>
                                                        {item.paymentType?.toLowerCase() === 'cheque' && (
                                                            <div className="reference-item">
                                                                <span className="label">Check No.</span>
                                                                <span className="value">{item.chequeNumber || 'N/A'}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-payments-message">
                                        <div className="message-content">
                                            <i className="fa-solid fa-receipt"></i>
                                            <h6>No Payment Records</h6>
                                            <p>No payments have been made for this loan yet.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col lg={5} className="loan-details">
                            {loan && (
                                <>
                                    <div className="details-section basic-info">
                                        <h5 className="section-title">
                                            <i className="fa-solid fa-info-circle"></i>
                                            Basic Information
                                        </h5>
                                        <Row>
                                            <Col md={4} className="title">Loan Processing Type</Col>
                                            <Col md={8} className="value">{loan.loanProcessingType}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Loan Type</Col>
                                            <Col md={8} className="value">{loan.loanType}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Loan Product</Col>
                                            <Col md={8} className="value">{loan.loanProduct}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Loan Security</Col>
                                            <Col md={8} className="value">{loan.loanSecurity}</Col>
                                        </Row>
                                    </div>

                                    <div className="details-section financial-info">
                                        <h5 className="section-title">
                                            <i className="fa-solid fa-money-bill-wave"></i>
                                            Financial Details
                                        </h5>
                                        <Row>
                                            <Col md={4} className="title">Amount Granted</Col>
                                            <Col md={8} className="value"><ToPeso amount={loan.amountGranted} /></Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Net Amount</Col>
                                            <Col md={8} className="value">
                                                {loan.advanceInterest ? (
                                                    <ToPeso amount={
                                                        loan.amountGranted - (
                                                            loan.serviceFee + 
                                                            loan.notarialFee + 
                                                            loan.docStamp + 
                                                            loan.insurance + 
                                                            (loan.amountGranted * (loan.interestRate / 100))
                                                        )
                                                    } />
                                                ) : (
                                                    <ToPeso amount={loan.amountGranted} />
                                                )}
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Interest Rate</Col>
                                            <Col md={8} className="value">{loan.interestRate}%</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Loan Term</Col>
                                            <Col md={8} className="value">{loan.loanTerm}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Date Granted</Col>
                                            <Col md={8} className="value">{format(new Date(loan.dateGranted), 'MMM dd, yyyy')}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Payment Interval</Col>
                                            <Col md={8} className="value">{loan.loanInterval}</Col>
                                        </Row>
                                    </div>

                                    <div className="details-section fees-info">
                                        <h5 className="section-title">
                                            <i className="fa-solid fa-file-invoice-dollar"></i>
                                            Fees & Charges
                                        </h5>
                                        <Row>
                                            <Col md={4} className="title">Service Fee</Col>
                                            <Col md={8} className="value">
                                                {loan.advanceInterest && <ToPeso amount={loan.serviceFee} />}
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Notarial Fee</Col>
                                            <Col md={8} className="value">
                                                {loan.advanceInterest && <ToPeso amount={loan.notarialFee} />}
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Doc Stamp</Col>
                                            <Col md={8} className="value">
                                                {loan.advanceInterest && <ToPeso amount={loan.docStamp} />}
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Insurance</Col>
                                            <Col md={8} className="value">
                                                {loan.advanceInterest && <ToPeso amount={loan.insurance} />}
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Advance Interest</Col>
                                            <Col md={8} className="value">{loan.advanceInterest ? "ADDED" : "NOT ADDED"}</Col>
                                        </Row>
                                    </div>

                                    <div className="details-section additional-info">
                                        <h5 className="section-title">
                                            <i className="fa-solid fa-circle-info"></i>
                                            Additional Information
                                        </h5>
                                        <Row>
                                            <Col md={4} className="title">Account Officer</Col>
                                            <Col md={8} className="value">{loan.accountOfficer}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Loan Ledger</Col>
                                            <Col md={8} className="value">{loan.loanLedger}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Reference</Col>
                                            <Col md={8} className="value">{loan.reference}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Beneficiary</Col>
                                            <Col md={8} className="value">{loan.addtl_beneficiary}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Contact</Col>
                                            <Col md={8} className="value">{loan.addtl_contact}</Col>
                                        </Row>
                                        <Row>
                                            <Col md={4} className="title">Relationship</Col>
                                            <Col md={8} className="value">{loan.addtl_relationship}</Col>
                                        </Row>
                                    </div>
                                </>
                            )}
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
}

export default PublicLoanPage;